import React from 'react'
import withStyles from '@material-ui/core/styles/withStyles'
import Container from '@material-ui/core/Container'
import Typography from '@material-ui/core/Typography'
import Link from '@material-ui/core/Link'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import Grid from '@material-ui/core/Grid'
import FacebookIcon from '@material-ui/icons/Facebook'
import InstagramIcon from '@material-ui/icons/Instagram'
import TwitterIcon from '@material-ui/icons/Twitter'
import YouTubeIcon from '@material-ui/icons/YouTube'
import PinterestIcon from '@material-ui/icons/Pinterest'
import LinkedInIcon from '@material-ui/icons/LinkedIn'
import HeadsetMicOutlinedIcon from '@material-ui/icons/HeadsetMicOutlined'
import clsx from 'clsx'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'
import { isDesktop, isMobile } from '../hooks/responsive'
import ExternalLink from './ExternalLink'
import styles from './Footer.styles'
import useDataLayer from '../hooks/useDataLayer'

const MINEGOCIO = 'minegocio'
const NATURA = 'natura'
const AVON = 'avon'

// eslint-disable-next-line complexity
const renderIconByType = type => {
  switch (type) {
    case 'facebook':
      return (
        <FacebookIcon
          data-gtm-identifier="button_text"
          fontSize={'small'}
          titleAccess="Facebook"
        />
      )
    case 'instagram':
      return (
        <InstagramIcon
          data-gtm-identifier="button_text"
          fontSize={'small'}
          titleAccess="Instagram"
        />
      )
    case 'twitter':
      return (
        <TwitterIcon
          data-gtm-identifier="button_text"
          fontSize={'small'}
          titleAccess="Twitter"
        />
      )
    case 'youtube':
      return (
        <YouTubeIcon
          data-gtm-identifier="button_text"
          fontSize={'small'}
          titleAccess="YouTube"
        />
      )
    case 'pinterest':
      return (
        <PinterestIcon
          data-gtm-identifier="button_text"
          fontSize={'small'}
          titleAccess="Pinterest"
        />
      )
    case 'linkedin':
      return (
        <LinkedInIcon
          data-gtm-identifier="button_text"
          fontSize={'small'}
          titleAccess="LinkedIn"
        />
      )
    default:
      return (
        <FacebookIcon
          data-gtm-identifier="button_text"
          fontSize={'small'}
          titleAccess="Facebook"
        />
      )
  }
}

const HelpLinks = ({ classes, setDataLayer, helpLinks }) => {
  if (helpLinks && helpLinks.length > 0) {
    return (
      <>
        <Typography
          variant="button"
          className={clsx(classes.title, isMobile() && classes.titleMobile)}
        >
          <FormattedMessage id="footerTitle" />
        </Typography>
        <Typography variant="caption" className={classes.links}>
          <List dense classes={isMobile() ? { root: classes.listMobile } : {}}>
            {helpLinks.map(linkItem => {
              const link = linkItem.node.frontmatter
              const linkLabel = link.label

              return (
                <ListItem
                  key={`footer-external-link-${link.dataTestId}`}
                  disableGutters
                >
                  <ExternalLink
                    id={link.linkId}
                    aria-label={link.ariaLabel}
                    color="inherit"
                    data-testid={link.dataTestId}
                    href={link.link}
                    key={`footer-external-link-${link.dataTestId}`}
                    title={linkItem.title}
                    onClick={() => setDataLayer(link.linkId, link.title)}
                    data-gtm-category-ga4="landing_footer"
                    data-gtm-label-ga4={linkLabel}
                    data-gtm-main-action-ga4="click"
                  >
                    {linkLabel}
                  </ExternalLink>
                </ListItem>
              )
            })}
          </List>
        </Typography>
      </>
    )
  }
  return null
}

const PhoneLinks = ({ classes, phoneLinks }) => {
  return (
    <List
      classes={{
        root: isMobile() ? classes.listMobile : classes.listDesktopPhoneList
      }}
    >
      {phoneLinks.map(linkItem => {
        const link = linkItem.node.frontmatter
        const linkLabel = link.label

        return (
          <ListItem key={`footer-list-${link.dataTestId}`}>
            <Link
              data-testid={link.dataTestId}
              href={link.link}
              underline={isDesktop() ? 'none' : 'hover'}
              color="inherit"
              component={isDesktop() ? 'p' : 'a'}
              data-gtm-category-ga4="landing_footer"
              data-gtm-label-ga4={linkLabel}
              data-gtm-main-action-ga4="click"
            >
              {linkLabel}
            </Link>
          </ListItem>
        )
      })}
    </List>
  )
}

const SocialMediaLinks = ({ classes, socialLinks, setDataLayer, company, isEloThemeActive = false }) => {
  return (
    <Grid
      classes={{ root: classes.socialIconsContainer }}
      container
      justify={(isEloThemeActive && isMobile()) ? 'space-around' : 'space-between'}
    >
      {socialLinks.map(linkItem => {
        const link = linkItem.node.frontmatter

        return (
          <ExternalLink
            id={link.linkId}
            aria-label={link.ariaLabel}
            color="inherit"
            data-testid={link.dataTestId}
            href={link.link}
            key={`footer-external-link-${link.dataTestId}`}
            title={linkItem.title}
            onClick={() => setDataLayer(link.linkId, link.title)}
            data-gtm-category-ga4="landing_footer"
            data-gtm-label-ga4={`${company}_${link.iconType}`}
            data-gtm-main-action-ga4="click"
          >
            {renderIconByType(link.iconType)}
          </ExternalLink>
        )
      })}
    </Grid>
  )
}

const filterCompanyLinks = (linkItem, company) => {
  const link = linkItem.node.frontmatter
  return link?.company === company
}

const SocialMediaLinksWithTitle = ({ classes, socialLinks, setDataLayer, title, company }) => {
  return (
    <div className={isMobile() ? classes.socialIconsMobileContainer : classes.socialIconsDesktopContainer}>
      <div>{title}</div>
      <SocialMediaLinks
        classes={classes}
        socialLinks={socialLinks}
        setDataLayer={setDataLayer}
        company={company}
        isEloThemeActive
      />
    </div>
  )
}

const SocialMediaLinksElo = ({ classes, socialLinks, setDataLayer }) => {
  const linksMinegocio = socialLinks.filter(linkItem => filterCompanyLinks(linkItem, MINEGOCIO))
  const linksNatura = socialLinks.filter(linkItem => filterCompanyLinks(linkItem, NATURA))
  const linksAvon = socialLinks.filter(linkItem => filterCompanyLinks(linkItem, AVON))

  return (
    <>
      {!!linksMinegocio.length && <SocialMediaLinksWithTitle
        classes={classes}
        socialLinks={linksMinegocio}
        setDataLayer={setDataLayer}
        company={'minegocio'}
        title={'Consultoría de Belleza'}
      /> }
      {!!linksNatura.length && <SocialMediaLinksWithTitle
        classes={classes}
        socialLinks={linksNatura}
        setDataLayer={setDataLayer}
        company={'natura'}
        title={<span>Sigue a <b>Natura</b> en:</span>}
      /> }
      {!!linksAvon.length && <SocialMediaLinksWithTitle
        classes={classes}
        socialLinks={linksAvon}
        setDataLayer={setDataLayer}
        company={'avon'}
        title={<span>Sigue a <b>Avon</b> en:</span>}
      /> }
    </>
  )
}

const Footer = ({ classes, socialLinks, phoneLinks, helpLinks, isEloThemeActive }) => {
  const dataLayer = useDataLayer()

  const setDataLayer = (buttonId, buttonText) => {
    dataLayer.push({
      event: 'buttonClick',
      buttonId,
      buttonText
    })
  }

  return (
    <footer
      id="footer"
      className={clsx(classes.footer, isMobile() && classes.footerMobile)}
      data-gtm-category-ga4="landing_footer"
      data-gtm-main-action-ga4="view"
      data-gtm-label-ga4="block_footer"
    >
      <Grid
        container
        direction="row"
        justify="center"
        className={clsx(
          classes.footerGrid,
          isMobile() && classes.footerGridMobile
        )}
      >
        <Container>
          <Grid
            container
            direction={isMobile() ? 'column' : null}
            justify={isMobile() ? 'center' : null}
            alignItems={isMobile() ? 'center' : null}
          >
            <Grid item xs={12} sm={9}>
              <HelpLinks
                classes={classes}
                helpLinks={helpLinks}
                setDataLayer={setDataLayer}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              {phoneLinks && phoneLinks.length > 0 && (
                <>
                  <Typography
                    variant="button"
                    className={clsx(
                      classes.title,
                      isMobile() && classes.titleMobile
                    )}
                  >
                    <HeadsetMicOutlinedIcon
                      fontSize="small"
                      classes={{ root: classes.headsetIcon }}
                    />{' '}
                    <FormattedMessage id="footerCallCenterLabel" />
                  </Typography>
                  <Typography variant="caption" className={classes.links}>
                    <PhoneLinks classes={classes} phoneLinks={phoneLinks} />
                    {isEloThemeActive ?
                      <SocialMediaLinksElo
                        classes={classes}
                        socialLinks={socialLinks}
                        setDataLayer={setDataLayer}
                      /> :
                      <SocialMediaLinks
                        classes={classes}
                        socialLinks={socialLinks}
                        setDataLayer={setDataLayer}
                      />
                    }
                  </Typography>
                </>
              )}
            </Grid>
          </Grid>
          <Typography
            variant="caption"
            className={clsx(
              classes.title,
              classes.copyright,
              isMobile() && classes.titleMobile
            )}
          >
            © {new Date().getFullYear()} Natura.{' '}
            <FormattedMessage id="footerCopyRightLabel" />
          </Typography>
        </Container>
      </Grid>
    </footer>
  )
}

Footer.propTypes = {
  classes: PropTypes.any,
  socialLinks: PropTypes.array,
  phoneLinks: PropTypes.array,
  helpLinks: PropTypes.array
}

export default withStyles(styles)(injectIntl(Footer))
